// Surrounding TERRAIN — the planet surface the arena sits on. A big low-poly heightfield centered on the
// level: FLAT under and around the playable deck (so the arena reads as a built platform), rising into
// biome relief further out — jagged scorched rock, rolling ice, dunes — that fades into the fog at the
// horizon. Biome-colored, lit by the same sun, no collision (visual only). Also exposes heightAt() so the
// decorative generator-sprawl can be planted on it.

import * as THREE from "three";
import type { Biome } from "./atmosphere";
import type { BBox } from "./perimeter";

// deterministic value-noise fbm (stable per position, no RNG)
function hash2(x: number, z: number): number {
  let h = (x | 0) * 374761393 + (z | 0) * 668265263;
  h = (h ^ (h >>> 13)) * 1274126177;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}
function vnoize(x: number, z: number): number {
  const xi = Math.floor(x), zi = Math.floor(z), xf = x - xi, zf = z - zi;
  const u = xf * xf * (3 - 2 * xf), v = zf * zf * (3 - 2 * zf);
  const a = hash2(xi, zi), b = hash2(xi + 1, zi), c = hash2(xi, zi + 1), d = hash2(xi + 1, zi + 1);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}
function fbm(x: number, z: number): number {
  let s = 0, amp = 0.5, f = 1;
  for (let i = 0; i < 4; i++) { s += amp * vnoize(x * f, z * f); f *= 2.03; amp *= 0.5; }
  return s;
}

export interface Terrain { mesh: THREE.Mesh; heightAt: (x: number, z: number) => number; dispose: () => void; }

const FLAT_Y = 140; // terrain plain sits just below the deck top (topY(0)=200) → arena is a low platform

// Distance LOD: a uniform grid wastes tris — a triangle at the fogged horizon is as small as one at the
// arena edge. We keep ONE grid topology (crack-free, no T-junctions) but radially WARP vertices so cells
// grow with distance from the arena, then run far fewer segments. Chebyshev radius c∈[0,1] is remapped
// c → c^LOD_POWER: near-arena rings stay dense, far rings stretch huge. ~4× fewer tris, near detail intact.
const SIZE = 72000;   // full span (edge sits far beyond fog)
const SEG = 108;      // segments/side (was 200) → 108²·2 = 23,328 tris (was 80,000)
const LOD_POWER = 2.1; // >1 = outer cells bigger; higher = more aggressive far coarsening

export function buildTerrain(bbox: BBox, biome: Biome): Terrain {
  const p = biome.preset;
  const cx = (bbox.minX + bbox.maxX) / 2, cz = (bbox.minZ + bbox.maxZ) / 2;
  const arenaR = 0.5 * Math.hypot(bbox.maxX - bbox.minX, bbox.maxZ - bbox.minZ) + 400;
  const ns = p.rough / 4200; // feature scale

  const heightAt = (x: number, z: number): number => {
    const d = Math.hypot(x - cx, z - cz);
    const mask = smoothstep(arenaR, arenaR * 2.4, d); // 0 flat under the arena, 1 far out
    if (mask === 0) return FLAT_Y; // under the arena the two fbm evals are multiplied out to nothing — skip them
    const n = fbm(x * ns, z * ns) - 0.5;
    const ridges = (fbm(x * ns * 2.3 + 40, z * ns * 2.3) - 0.5) * 0.4; // finer relief
    return FLAT_Y + (n * 2 + ridges) * p.relief * mask;
  };

  const geo = new THREE.PlaneGeometry(SIZE, SIZE, SEG, SEG);
  geo.rotateX(-Math.PI / 2); // lie flat in XZ; local x,z now map to world x,z
  const pos = geo.getAttribute("position") as THREE.BufferAttribute;
  const half = SIZE / 2;
  const colAttr = new Float32Array(pos.count * 3);
  const glo = new THREE.Color(p.ground), ghi = new THREE.Color(p.ground2);
  const tmp = new THREE.Color();
  for (let i = 0; i < pos.count; i++) {
    // radial LOD warp: push each vertex outward along its square ring so far cells grow, near stay fine
    let lx = pos.getX(i), lz = pos.getZ(i);
    const c = Math.max(Math.abs(lx), Math.abs(lz)) / half; // Chebyshev radius 0..1
    if (c > 1e-6) {
      const scale = Math.pow(c, LOD_POWER) / c;
      lx *= scale; lz *= scale;
      pos.setX(i, lx); pos.setZ(i, lz);
    }
    const x = lx + cx, z = lz + cz;
    const y = heightAt(x, z);
    pos.setY(i, y - FLAT_Y); // mesh is positioned at FLAT_Y; store relative height
    const t = clamp01((y - FLAT_Y) / (p.relief + 1) + 0.5 + (vnoize(x * ns * 5, z * ns * 5) - 0.5) * 0.4);
    tmp.copy(glo).lerp(ghi, t);
    colAttr[i * 3] = tmp.r; colAttr[i * 3 + 1] = tmp.g; colAttr[i * 3 + 2] = tmp.b;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colAttr, 3));
  geo.computeVertexNormals();

  const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.97, metalness: 0, flatShading: true });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(cx, FLAT_Y, cz);
  mesh.receiveShadow = true;
  mesh.renderOrder = -1;

  return {
    mesh, heightAt,
    dispose: () => { geo.dispose(); mat.dispose(); },
  };
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
function smoothstep(a: number, b: number, x: number): number {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
}
